import React from 'react';
import { useFamily } from '../../context/FamilyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { deleteActivity } from '@coparent/shared/src/services/activityService';
import { DAYS_OF_WEEK_SHORT } from '@coparent/shared/src/utils/constants';
import { getChildIds } from '@coparent/shared/src/utils/childUtils';
import ChildDots from '../common/ChildDots';
import LinkifiedText from '../common/LinkifiedText';
import { HiClock, HiLocationMarker, HiCurrencyDollar, HiTrash, HiPencil, HiRefresh, HiCalendar, HiDocumentText, HiPaperClip } from 'react-icons/hi';
import DiscussButton from '../common/DiscussButton';
import { formatCurrency } from '../../utils/expenseUtils';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import toast from 'react-hot-toast';

const ActivityList = ({ activities, onEdit }) => {
  const { familyId, family, childrenMap } = useFamily();
  const { canDelete } = usePermissions();

  const handleDelete = async (activityId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await deleteActivity(familyId, activityId);
      toast.success('Activity deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (!activities.length) {
    return (
      <div className="text-center py-16 text-ink-300">
        <p className="text-base font-semibold">No activities yet</p>
        <p className="text-sm mt-1">Add your child's activities to track logistics.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {activities.map(activity => (
        <div key={activity.id} className="card-hover p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: activity.color || '#22c55e' }}
              />
              <h3 className="font-bold text-ink-900">{activity.name}</h3>
            </div>
            <div className="flex items-center gap-1">
              <DiscussButton
                type="activity"
                id={activity.id}
                label={activity.name}
                childIds={getChildIds(activity)}
              />
              <button
                onClick={() => onEdit(activity)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
              >
                <HiPencil className="text-sm" />
              </button>
              {canDelete && (
                <button
                  onClick={() => handleDelete(activity.id)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
                >
                  <HiTrash className="text-sm" />
                </button>
              )}
            </div>
          </div>

          {/* Schedule info */}
          <div className="flex items-center gap-2 mt-3">
            {activity.type === 'event' ? (
              <div className="flex items-center gap-1.5">
                <HiCalendar className="text-ink-400 text-xs" />
                <span className="text-[0.6875rem] font-semibold text-ink-600">
                  {activity.eventDate ? formatDate(activity.eventDate, 'MMM d, yyyy') : 'No date set'}
                </span>
                <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-blue-50 text-blue-600 ml-1">
                  One-off
                </span>
              </div>
            ) : activity.frequency === 'monthly' ? (
              <div className="flex items-center gap-1.5">
                <HiCalendar className="text-ink-400 text-xs" />
                <span className="text-[0.6875rem] font-semibold text-ink-600">
                  {activity.monthlyDayOfMonth}{activity.monthlyDayOfMonth === 1 || activity.monthlyDayOfMonth === 21 || activity.monthlyDayOfMonth === 31 ? 'st' : activity.monthlyDayOfMonth === 2 || activity.monthlyDayOfMonth === 22 ? 'nd' : activity.monthlyDayOfMonth === 3 || activity.monthlyDayOfMonth === 23 ? 'rd' : 'th'} of each month
                </span>
                <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-emerald-50 text-emerald-600 flex items-center gap-1 ml-1">
                  <HiRefresh className="text-[0.5rem]" />
                  Monthly
                </span>
              </div>
            ) : (
              <>
                <div className="flex gap-1.5">
                  {DAYS_OF_WEEK_SHORT.map((day, i) => (
                    <span
                      key={i}
                      className={`text-[0.625rem] px-2 py-1 rounded-lg font-bold uppercase tracking-wide ${
                        activity.dayOfWeek?.includes(i)
                          ? 'bg-ink-800 text-white'
                          : 'bg-surface-100 text-ink-300'
                      }`}
                    >
                      {day}
                    </span>
                  ))}
                </div>
                <span className="text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide bg-emerald-50 text-emerald-600 flex items-center gap-1">
                  <HiRefresh className="text-[0.5rem]" />
                  {activity.frequency === 'biweekly' ? 'Biweekly' : 'Weekly'}
                </span>
              </>
            )}
          </div>

          {/* Time */}
          {activity.startTime && (
            <div className="flex items-center gap-2 mt-3 text-sm text-ink-500">
              <HiClock className="text-ink-300 text-xs" />
              <span className="font-medium">
                {activity.startTime}
                {activity.endTime && ` - ${activity.endTime}`}
              </span>
            </div>
          )}

          {/* Location */}
          {activity.location && (
            <div className="flex items-center gap-2 mt-1.5 text-sm text-ink-500">
              <HiLocationMarker className="text-ink-300 text-xs" />
              <span className="font-medium">{activity.location}</span>
            </div>
          )}

          {/* Cost */}
          {activity.cost && (
            <div className="flex items-center gap-2 mt-1.5 text-sm text-ink-500">
              <HiCurrencyDollar className="text-ink-300 text-xs" />
              <span className="font-medium">{formatCurrency(activity.cost)}/session</span>
            </div>
          )}

          {/* Notes */}
          {activity.notes && (
            <div className="flex items-start gap-2 mt-1.5 text-sm text-ink-500">
              <HiDocumentText className="text-ink-300 text-xs mt-1 flex-shrink-0" />
              <LinkifiedText text={activity.notes} className="font-medium" />
            </div>
          )}

          {/* Attachments */}
          {activity.attachments?.length > 0 && (
            <div className="flex items-center gap-2 mt-1.5 text-sm text-ink-500">
              <HiPaperClip className="text-ink-300 text-xs flex-shrink-0" />
              <span className="font-medium">
                {activity.attachments.length} attachment{activity.attachments.length > 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Children */}
          {getChildIds(activity).length > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              <ChildDots childIds={getChildIds(activity)} childrenMap={childrenMap} showNames />
            </div>
          )}

          {/* Parent assignment */}
          {family && (
            <div className="mt-3 pt-3 border-t border-surface-100 flex gap-4 text-xs text-ink-400">
              <span>
                Drop-off:{' '}
                <span className="font-semibold text-ink-600">
                  {activity.dropoffParentId === 'self'
                    ? 'Self arrival'
                    : activity.dropoffParentId
                      ? family.parentProfiles[activity.dropoffParentId]?.displayName || 'Custody parent'
                      : 'Custody parent'
                  }
                </span>
              </span>
              <span>
                Pick-up:{' '}
                <span className="font-semibold text-ink-600">
                  {activity.pickupParentId === 'self'
                    ? 'Self dismissal'
                    : activity.pickupParentId
                      ? family.parentProfiles[activity.pickupParentId]?.displayName || 'Custody parent'
                      : 'Custody parent'
                  }
                </span>
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ActivityList;
